import { MultiSelectField } from "@overmap-ai/forms"
import type { ReactNode } from "react"

import {
	NullableStringArrayEqualsFilterModifier,
	NullableStringArrayNotEqualsFilterModifier,
	StringArrayEveryFilterModifier,
	StringArrayNoneFilterModifier,
	StringArraySomeFilterModifier,
} from "../../modifiers"
import { BaseFilterDef, createFilterModifierConfig, type FilterDefRenderProps } from "../BaseFilterDef"
import { MultiSelectFieldFilterCell } from "./MultiSelectFieldFilterCell"

type MultiSelectFieldValue = string[] | null
type MultiSelectFilterValue = string[] | null

export class MultiSelectFieldFilterDef extends BaseFilterDef<MultiSelectFieldValue, MultiSelectFilterValue> {
	readonly id: string
	readonly defaultFilterValue: MultiSelectFilterValue | undefined = undefined
	readonly defaultFilterModifier: keyof typeof this.modifiers = "equals"

	readonly field: MultiSelectField

	constructor(field: MultiSelectField) {
		super()
		this.field = field
		this.id = field.id
	}

	readonly modifiers = {
		equals: createFilterModifierConfig({
			modifier: NullableStringArrayEqualsFilterModifier,
			isValueValid: (_value: MultiSelectFieldValue): _value is string[] | null => true,
			isFilterValueValid: (_filterValue: MultiSelectFilterValue): _filterValue is string[] | null => true,
		}),
		notEquals: createFilterModifierConfig({
			modifier: NullableStringArrayNotEqualsFilterModifier,
			isValueValid: (_value: MultiSelectFieldValue): _value is string[] | null => true,
			isFilterValueValid: (_filterValue: MultiSelectFilterValue): _filterValue is string[] | null => true,
		}),
		every: createFilterModifierConfig({
			modifier: StringArrayEveryFilterModifier,
			isValueValid: (value: MultiSelectFieldValue): value is string[] => Array.isArray(value),
			isFilterValueValid: (filterValue: MultiSelectFilterValue): filterValue is string[] =>
				Array.isArray(filterValue),
		}),
		some: createFilterModifierConfig({
			modifier: StringArraySomeFilterModifier,
			isValueValid: (value: MultiSelectFieldValue): value is string[] => Array.isArray(value),
			isFilterValueValid: (filterValue: MultiSelectFilterValue): filterValue is string[] =>
				Array.isArray(filterValue),
		}),
		none: createFilterModifierConfig({
			modifier: StringArrayNoneFilterModifier,
			isValueValid: (value: MultiSelectFieldValue): value is string[] => Array.isArray(value),
			isFilterValueValid: (filterValue: MultiSelectFilterValue): filterValue is string[] =>
				Array.isArray(filterValue),
		}),
	}

	render(props: FilterDefRenderProps<MultiSelectFieldFilterDef>): ReactNode {
		return <MultiSelectFieldFilterCell key={props.filter.id} field={this.field} {...props} />
	}
}
